import { useEffect } from "react";
import { useDataFastGoals } from "@/hooks/useDataFastGoals";

/** Tempo com a página visível que separa uma visita de um carregamento. */
const VISIBILIDADE_MINIMA_MS = 5000;

/** Eventos que só um humano produz — cada um ainda checado por `isTrusted`. */
const EVENTOS_DE_INTERACAO = ["pointerdown", "keydown", "scroll"] as const;

/**
 * De onde a página foi aberta, do ponto de vista do user agent. O que importa
 * aqui é isolar os navegadores internos do Meta: são eles que pré-carregam a
 * landing e produzem o carregamento sem gente.
 */
function detectarNavegador(): string {
  const ua = navigator.userAgent;
  if (/FBAN|FBAV|FB_IAB/i.test(ua)) return "facebook_app";
  if (/Instagram/i.test(ua)) return "instagram_app";
  return "outro";
}

/**
 * Dispara `engaged_view` uma vez por carregamento, e só quando as duas coisas
 * aconteceram: a página ficou cinco segundos **visível** e houve uma interação
 * confiável.
 *
 * As duas condições existem para responder a uma pergunta que o `landing_view`
 * não responde: quem entrou de verdade. O `landing_view` mede que a página
 * carregou, e carregar não é entrar — o navegador interno do Facebook e do
 * Instagram pré-carrega a landing do anúncio enquanto ele ainda está rolando
 * na tela, antes de qualquer clique. Esse preload roda o JavaScript num
 * webview escondido: o DataFast registra pageview, o goal dispara, e ninguém
 * nunca viu a página. O mesmo vale para o prerender do Chrome.
 *
 * A defesa é o relógio só andar em `visibilityState === "visible"`. Página
 * pré-carregada nasce oculta e só vira visível se a pessoa clicar no anúncio;
 * enquanto isso o contador fica parado, e o goal nunca chega ao limite. Não
 * há tratamento especial de prerender porque não é preciso: ele também é
 * oculto.
 *
 * A interação é a segunda trava, para o caso de uma aba aberta e esquecida.
 * `isTrusted` descarta evento sintético. O `scroll` disparado pelo
 * `scrollIntoView` dos CTAs também é confiável, mas ele só acontece depois de
 * um clique — que já teria contado como interação de qualquer forma.
 *
 * O `landing_view` continua onde está, de propósito: a razão entre os dois,
 * filtrada por `browser`, é a medida de quanto do tráfego pago é preload.
 */
export function useEngagedView(surface: string) {
  const { trackCustomGoal } = useDataFastGoals();

  useEffect(() => {
    let visivelMs = 0;
    let interagiu = false;
    let intervalo = 0;

    const marcarInteracao = (event: Event) => {
      if (event.isTrusted) interagiu = true;
    };

    const encerrar = () => {
      window.clearInterval(intervalo);
      EVENTOS_DE_INTERACAO.forEach((nome) =>
        window.removeEventListener(nome, marcarInteracao, { capture: true }),
      );
    };

    EVENTOS_DE_INTERACAO.forEach((nome) =>
      window.addEventListener(nome, marcarInteracao, {
        passive: true,
        capture: true,
      }),
    );

    intervalo = window.setInterval(() => {
      if (document.visibilityState === "visible") visivelMs += 1000;
      if (!interagiu || visivelMs < VISIBILIDADE_MINIMA_MS) return;

      trackCustomGoal("engaged_view", {
        surface,
        browser: detectarNavegador(),
      });
      encerrar();
    }, 1000);

    return encerrar;
  }, [surface, trackCustomGoal]);
}
